import React from "react"
import {Box, Link, Typography} from "@mui/material"
import {Link as RouterLink} from "react-router-dom"

import type {IdentificationHint, UnidentifiedCandidate} from "../types"

import {HintChips} from "./HintChips"

import {colors} from "@indebted/theme/colors"

const strategyLabels: Record<string, string> = {
	SenderAddress: "Matched on the sender address",
	SenderPhone: "Matched on the sender number",
	ThreadCustomer: "Matched on an earlier message in this thread",
}

function CandidateCard({
	candidate,
	hints = [],
	actions,
	facts,
}: {
	candidate: UnidentifiedCandidate
	hints?: IdentificationHint[]
	actions?: React.ReactNode
	facts?: React.ReactNode
}) {
	return (
		<Box
			px={3}
			py={2.5}
			display="flex"
			flexDirection="column"
			gap={1.5}
			borderRadius={2}
			sx={{border: "1px solid", borderColor: colors.gray["200"], bgcolor: "background.paper"}}
		>
			<Typography variant="subtitle2">{candidate.Name}</Typography>
			<Typography variant="caption" color="textSecondary">
				{candidate.Client} · {strategyLabels[candidate.Strategy] ?? candidate.Strategy}
			</Typography>
			{facts}
			<HintChips hints={hints} />
			<Box display="flex" alignItems="center" gap={1.5}>
				{actions}
				<Link
					component={RouterLink}
					to={`/customers/${candidate.CustomerID}`}
					variant="body2"
					underline="hover"
				>
					Open customer
				</Link>
			</Box>
		</Box>
	)
}

function CandidateList({
	candidates,
	renderActions,
}: {
	candidates: UnidentifiedCandidate[]
	renderActions?: (candidate: UnidentifiedCandidate) => React.ReactNode
}) {
	if (candidates.length === 0) {
		return (
			<Typography variant="body2" color="textSecondary">
				No candidate. Search for the customer to assign this message.
			</Typography>
		)
	}

	return (
		<Box display="flex" flexDirection="column" gap={2}>
			<Typography variant="body2" fontWeight={600}>
				{candidates.length > 1 ? `${candidates.length} candidate records` : "Candidate"}
			</Typography>
			{candidates.map((candidate) => (
				<CandidateCard key={candidate.CustomerID} candidate={candidate} actions={renderActions?.(candidate)} />
			))}
		</Box>
	)
}

export {CandidateCard, CandidateList}
